import {KMDGameAssets} from "../KMDGameAssets";
import {KennethMustDie} from "../KennethMustDie";
import {gsap} from "gsap";
import {KMDSound} from "../sound/KMDSound";

export class KMDMultipliersChain extends PIXI.Container {

    private _handChain: PIXI.spine.Spine;
    private _balls: PIXI.Sprite[];
    private _ballsContainer: PIXI.Container;
    private _multipliers: PIXI.BitmapText[];
    private _updateOnResultOnce: boolean = true;

    constructor() {
        super();
        this.name = "KMDMultipliersChain";

        this.init();
    }

    public onRestore(canPlaySound: boolean): void {
        this.onResult(canPlaySound);

        this._updateOnResultOnce = (KennethMustDie.model.data.mode.indexOf("NORMAL") > -1 && KennethMustDie.model.data.nextMode.indexOf("NORMAL") === -1); // true, if entering any FS, otherwise false

        // ball and multiplier should look like consumed as in normal game play
        for (let ctr: number = 0; ctr < this._balls.length; ctr++) {
            this._balls[ctr].alpha = this._multipliers[ctr].alpha = 0.5;
        }
    }

    public startSpin(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // #276 => any of the bonus mode
        if (KennethMustDie.model.data.mode.indexOf("NORMAL") === -1 && KennethMustDie.model.data.nextMode.indexOf("NORMAL") === -1) {
            tl.add(() => {
                this._multipliers.forEach((multi: PIXI.BitmapText) => {
                    multi.alpha = 1;
                });
                this._balls.forEach((ball: PIXI.Sprite) => {
                    ball.alpha = 1;
                });
            });

            return tl;
        }

        // #528 -> pt 5
        for (let ctr: number = 0; ctr < this._multipliers.length; ctr++) {
            const ball: PIXI.Sprite = this._balls[ctr];

            tl.to([ball, this._multipliers[ctr]], {
                duration: 0.1,
                x: 105,
                ease: "none"
            }, 0);
            tl.to([ball.scale, this._multipliers[ctr].scale], {
                duration: 0.1,
                x: 0.75,
                y: 0.75,
                ease: "none"
            }, 0);
        }

        tl.add(() => {
            this._ballsContainer.removeChildren();
            this._multipliers.forEach((multi: PIXI.BitmapText) => {
                this.removeChild(multi);
            });
        });

        return tl;
    }

    public onResult(canPlaySound: boolean): gsap.core.Timeline {
        // #276 => any of the bonus mode
        if (!this._updateOnResultOnce && KennethMustDie.model.data.mode.indexOf("NORMAL") === -1) {
            return gsap.timeline();
        }

        this._updateOnResultOnce = false;

        // creation has to be done before the timeline
        this._balls = [];
        this._multipliers = [];

        return this.showMultipliers(1, canPlaySound);
    }

    public jumpBall(idx: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            multiplier: PIXI.BitmapText = this._multipliers[idx],
            multiCopy: PIXI.BitmapText = new PIXI.BitmapText("", {
                fontName: KMDGameAssets.BITMAP_FONT_SYM_COUNT,
                fontSize: 12
            });

        tl.to([this._balls[idx], multiplier], {
            duration: 0.1,
            alpha: 0.5
        }, 0);

        if ((+multiplier.text.split("X")[1]) > 1) {
            multiCopy.anchor = new PIXI.Point(0.5);

            tl.add(() => {
                multiCopy.position = multiplier.position;

                multiCopy.text = multiplier.text;
                this.addChild(multiCopy);
            }, 0);
            tl.to(multiCopy, {
                duration: 0.3,
                y: "-=80"
            }, 0);

            tl.add(() => {
                this.removeChild(multiCopy);
            });
        }

        return tl;
    }

    public removeMultiplier(idx: number): void {
        this._ballsContainer.removeChild(this._balls[idx]);
        this.removeChild(this._multipliers[idx]);
    }

    public collapse(): void {
        this._balls = this._balls.filter((val: PIXI.Sprite) => {
            return (val.parent);
        });
        this._multipliers = this._multipliers.filter((val: PIXI.BitmapText) => {
            return (val.parent);
        });

        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.333;

        tl.add(() => {
            this._handChain.state.setAnimation(0, "pull", false);
        });
        for (let ctr: number = 0; ctr < this._multipliers.length; ctr++) {
            const ball: PIXI.Sprite = this._balls[ctr];

            if (ball.x !== ((ctr * KennethMustDie.SYMBOL_WIDTH) + 216)) {
                tl.to([ball, this._multipliers[ctr]], {
                    duration: duration,
                    x: (ctr * KennethMustDie.SYMBOL_WIDTH) + 216,
                    ease: "none"
                }, 0);
            }
        }
    }

    public fillUp(): gsap.core.Timeline {
        return this.showMultipliers(this._multipliers.length + 1, true);
    }

    public onBonusGameIntro(): void {
        this._updateOnResultOnce = true;
    }

    // #281
    public turnOffForCFS(): void {
        this.alpha = 0;
    }

    // #281
    public transitionForCFS(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this, {
            duration: duration,
            alpha: 1
        });

        return tl;
    }

    private init(): void {
        this._handChain = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("handChain"));
        this._handChain.position.set(361, 684);
        this._handChain.state.setAnimation(0, "pull", false);

        this._ballsContainer = new PIXI.Container();

        this.addChild(this._handChain, this._ballsContainer);

        this._balls = [];
        this._multipliers = [];

        this.onRestore(false);
    }

    private showMultipliers(seed: number, canPlaySound: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.333,
            totalBalls: number = 3;

        for (let ctr: number = seed; ctr <= totalBalls; ctr++) {
            this.createBallAndMulti(ctr);
        }

        tl.add(() => {
            this._handChain.state.setAnimation(0, "pull", false);
        }, 0);

        for (let ctr: number = (seed - 1); ctr < this._balls.length; ctr++) {
            const ball: PIXI.Sprite = this._balls[ctr],
                multiValue: number = KennethMustDie.model.data.multiplier[ctr + 1][3];

            // #250
            tl.add(() => {
                if (canPlaySound) {
                    let soundIdx: number = 0;

                    // #449
                    if (multiValue > 10) {
                        soundIdx = 2;
                    } else if (multiValue > 5 && multiValue < 11) {
                        soundIdx = 1;
                    }
                    KennethMustDie.soundPlayer.playSound(KMDSound.GLORY_HOLE_NEW_BALL[soundIdx]);
                }
                this._ballsContainer.addChild(ball);
            }, 0);
            tl.to(ball.scale, {
                duration: duration,
                x: 1,
                y: 1
            }, 0);
            tl.to(ball, {
                duration: duration,
                x: (ctr * KennethMustDie.SYMBOL_WIDTH) + 216
            }, 0);

            tl.add(() => {
                this._multipliers[ctr].position.set(ball.x, (ball.y - this._multipliers[ctr].height / 2));
                this._multipliers[ctr].visible = true;
            });
        }

        return tl;
    }

    private createBallAndMulti(ctr: number): void {
        const multiValue: number = KennethMustDie.model.data.multiplier[ctr][3];

        if (multiValue < 1) {
            return;
        }

        const ball: PIXI.Sprite = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("special_reel_assets/golden_ball")[0]);
        ball.anchor.set(0.5);
        ball.position.set(590, 681);
        ball.scale.set(0.7);
        this._balls.push(ball);

        const multi: PIXI.BitmapText = new PIXI.BitmapText("X" + multiValue, {
            fontName: KMDGameAssets.BITMAP_FONT_SYM_COUNT,
            fontSize: 12
        });
        multi.visible = false;
        multi.anchor = new PIXI.Point(0.5);
        this._multipliers.push(multi);
        this.addChild(multi);
    }
}